import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/Button";
import {
  dismissInboxNotification,
  fetchInboxNotifications,
  fetchInboxUnreadCount,
  markAllInboxNotificationsRead,
  markInboxNotificationRead,
} from "@/services/marketingApi";
import { cn } from "@/utils/cn";
import { useUiStore } from "@/store/uiStore";

export function NotificationBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const t = useUiStore((s) => s.t);
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const unread = useQuery({
    queryKey: ["marketing", "inbox-unread"],
    queryFn: fetchInboxUnreadCount,
    staleTime: 60_000,
    refetchInterval: (query) => {
      const status = (query.state.error as { response?: { status?: number } } | null)?.response?.status;
      if (status === 429) return false;
      return 15_000;
    },
    refetchIntervalInBackground: true,
  });

  const list = useQuery({
    queryKey: ["marketing", "inbox", "preview"],
    queryFn: () => fetchInboxNotifications({ limit: 8 }),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: markInboxNotificationRead,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["marketing", "inbox"] });
      void qc.invalidateQueries({ queryKey: ["marketing", "inbox-unread"] });
    },
  });

  const markAll = useMutation({
    mutationFn: markAllInboxNotificationsRead,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["marketing", "inbox"] });
      void qc.invalidateQueries({ queryKey: ["marketing", "inbox-unread"] });
      toast.success(t("ui.action.markedAllRead"));
    },
  });

  const dismiss = useMutation({
    mutationFn: dismissInboxNotification,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["marketing", "inbox"] });
      void qc.invalidateQueries({ queryKey: ["marketing", "inbox-unread"] });
    },
  });

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!panelRef.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const count = unread.data || 0;

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="ghost"
        size="sm"
        aria-label="Notifications"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="relative"
      >
        <Bell className="h-4 w-4" />
        {count > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--amos-brand)] px-1 text-[9px] font-semibold text-white">
            {count > 9 ? "9+" : count}
          </span>
        ) : null}
      </Button>

      {open ? (
        <div className="absolute right-0 z-40 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-zinc-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-950">
          <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-2 dark:border-zinc-800">
            <div className="text-sm font-semibold">Notifications</div>
            <button
              type="button"
              className="text-[11px] font-medium text-[var(--amos-brand)] disabled:opacity-40"
              disabled={markAll.isPending || count === 0}
              onClick={() => markAll.mutate()}
            >
              Mark all read
            </button>
          </div>
          <ul className="max-h-80 overflow-y-auto">
            {list.isLoading ? (
              <li className="px-3 py-6 text-center text-xs text-zinc-500">Loading…</li>
            ) : !list.data?.items.length ? (
              <li className="px-3 py-6 text-center text-xs text-zinc-500">You are all caught up</li>
            ) : (
              list.data.items.map((n) => (
                <li
                  key={n.id}
                  className={cn(
                    "border-b border-zinc-50 px-3 py-2.5 last:border-0 dark:border-zinc-900",
                    !n.read_at && "bg-sky-50/40 dark:bg-sky-950/20",
                  )}
                >
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => {
                      if (!n.read_at) markRead.mutate(n.id);
                      setOpen(false);
                      if (n.path) navigate(n.path);
                      else navigate("/notifications");
                    }}
                  >
                    <div className="text-xs font-semibold text-zinc-900 dark:text-zinc-50">{n.title}</div>
                    {n.body ? (
                      <div className="mt-0.5 line-clamp-2 text-[11px] text-zinc-500">{n.body}</div>
                    ) : null}
                  </button>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-zinc-400">{n.priority}</span>
                    <button
                      type="button"
                      className="text-[10px] text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200"
                      onClick={() => dismiss.mutate(n.id)}
                    >
                      Dismiss
                    </button>
                  </div>
                </li>
              ))
            )}
          </ul>
          <div className="border-t border-zinc-100 px-3 py-2 dark:border-zinc-800">
            <Link
              to="/notifications"
              className="text-xs font-medium text-[var(--amos-brand)]"
              onClick={() => setOpen(false)}
            >
              View all notifications
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
